// Shipping rates based on US zones (state groups) and package weight
// Zone 1: Local (nearby states) - cheapest
// Zone 2: Regional - moderate
// Zone 3: Cross-country - most expensive

interface ShippingZone {
  zone: number;
  label: string;
  standardRate: number;
  expressRate: number;
  overnightRate: number;
}

// Weight brackets in ounces (oz)
// 1 lb = 16 oz
interface WeightBracket {
  maxOz: number;
  label: string;
  multiplier: number; // Multiplier applied to base rate
}

const weightBrackets: WeightBracket[] = [
  { maxOz: 8, label: '0-8 oz', multiplier: 1.0 },      // Up to 0.5 lb
  { maxOz: 16, label: '8-16 oz', multiplier: 1.3 },    // 0.5-1 lb
  { maxOz: 32, label: '1-2 lbs', multiplier: 1.6 },    // 1-2 lbs
  { maxOz: 80, label: '2-5 lbs', multiplier: 2.0 },    // 2-5 lbs
  { maxOz: 160, label: '5-10 lbs', multiplier: 2.5 },  // 5-10 lbs
  { maxOz: Infinity, label: '10+ lbs', multiplier: 3.0 }, // 10+ lbs
];

// Carrier-specific rates by weight and zone
interface CarrierService {
  name: string;
  baseRate: number;
  daysMin: number;
  daysMax: number;
}

interface CarrierRates {
  carrier: string;
  services: Record<string, CarrierService>;
}

const carrierRatesByWeight: CarrierRates[] = [
  {
    carrier: 'UPS',
    services: {
      ground: { name: 'UPS Ground', baseRate: 6.99, daysMin: 5, daysMax: 8 },
      threeDay: { name: 'UPS 3 Day Select', baseRate: 14.99, daysMin: 3, daysMax: 3 },
      twoDay: { name: 'UPS 2nd Day Air', baseRate: 22.99, daysMin: 2, daysMax: 2 },
      nextDay: { name: 'UPS Next Day Air', baseRate: 34.99, daysMin: 1, daysMax: 1 },
    },
  },
  {
    carrier: 'FedEx',
    services: {
      ground: { name: 'FedEx Ground', baseRate: 5.99, daysMin: 5, daysMax: 7 },
      express: { name: 'FedEx Express Saver', baseRate: 16.99, daysMin: 3, daysMax: 3 },
      twoDay: { name: 'FedEx 2Day', baseRate: 24.99, daysMin: 2, daysMax: 2 },
      overnight: { name: 'FedEx Overnight', baseRate: 32.99, daysMin: 1, daysMax: 1 },
    },
  },
  {
    carrier: 'DHL',
    services: {
      standard: { name: 'DHL Standard', baseRate: 7.49, daysMin: 5, daysMax: 8 },
      express: { name: 'DHL Express', baseRate: 18.99, daysMin: 3, daysMax: 4 },
      worldwide: { name: 'DHL Express Worldwide', baseRate: 29.99, daysMin: 1, daysMax: 2 },
    },
  },
  {
    carrier: 'USPS',
    services: {
      firstClass: { name: 'USPS First Class', baseRate: 3.99, daysMin: 3, daysMax: 5 },
      priority: { name: 'USPS Priority Mail', baseRate: 8.99, daysMin: 2, daysMax: 3 },
      express: { name: 'USPS Priority Mail Express', baseRate: 26.99, daysMin: 1, daysMax: 2 },
    },
  },
];

// State to zone mapping
const stateZones: Record<string, number> = {
  // Zone 1 - East Coast / Local
  'New York': 1, 'NY': 1,
  'New Jersey': 1, 'NJ': 1,
  'Connecticut': 1, 'CT': 1,
  'Pennsylvania': 1, 'PA': 1,
  'Massachusetts': 1, 'MA': 1,
  'Delaware': 1, 'DE': 1,
  'Maryland': 1, 'MD': 1,
  'Virginia': 1, 'VA': 1,
  'Rhode Island': 1, 'RI': 1,
  'Vermont': 1, 'VT': 1,
  'New Hampshire': 1, 'NH': 1,
  'Maine': 1, 'ME': 1,
  'District of Columbia': 1, 'DC': 1,
  'West Virginia': 1, 'WV': 1,

  // Zone 2 - Southeast / Midwest
  'North Carolina': 2, 'NC': 2,
  'South Carolina': 2, 'SC': 2,
  'Georgia': 2, 'GA': 2,
  'Florida': 2, 'FL': 2,
  'Ohio': 2, 'OH': 2,
  'Michigan': 2, 'MI': 2,
  'Indiana': 2, 'IN': 2,
  'Illinois': 2, 'IL': 2,
  'Wisconsin': 2, 'WI': 2,
  'Minnesota': 2, 'MN': 2,
  'Iowa': 2, 'IA': 2,
  'Missouri': 2, 'MO': 2,
  'Kentucky': 2, 'KY': 2,
  'Tennessee': 2, 'TN': 2,
  'Alabama': 2, 'AL': 2,
  'Mississippi': 2, 'MS': 2,
  'Louisiana': 2, 'LA': 2,
  'Arkansas': 2, 'AR': 2,
  'Oklahoma': 2, 'OK': 2,
  'Kansas': 2, 'KS': 2,
  'Nebraska': 2, 'NE': 2,
  'South Dakota': 2, 'SD': 2,
  'North Dakota': 2, 'ND': 2,

  // Zone 3 - West / Far states
  'Texas': 3, 'TX': 3,
  'Colorado': 3, 'CO': 3,
  'New Mexico': 3, 'NM': 3,
  'Arizona': 3, 'AZ': 3,
  'Utah': 3, 'UT': 3,
  'Nevada': 3, 'NV': 3,
  'California': 3, 'CA': 3,
  'Oregon': 3, 'OR': 3,
  'Washington': 3, 'WA': 3,
  'Idaho': 3, 'ID': 3,
  'Montana': 3, 'MT': 3,
  'Wyoming': 3, 'WY': 3,
  'Hawaii': 3, 'HI': 3,
  'Alaska': 3, 'AK': 3,
};

const zoneRates: Record<number, ShippingZone> = {
  1: {
    zone: 1,
    label: 'Zone locale',
    standardRate: 4.99,
    expressRate: 7.99,
    overnightRate: 14.99,
  },
  2: {
    zone: 2,
    label: 'Zone régionale',
    standardRate: 5.99,
    expressRate: 12.99,
    overnightRate: 22.99,
  },
  3: {
    zone: 3,
    label: 'Zone éloignée',
    standardRate: 9.99,
    expressRate: 17.99,
    overnightRate: 29.99,
  },
};

// Major city surcharges (for remote/rural areas) or discounts (for major hubs)
const cityAdjustments: Record<string, number> = {
  // Major hubs - small discount
  'new york': -1,
  'los angeles': -1,
  'chicago': -1,
  'houston': -0.5,
  'phoenix': -0.5,
  'philadelphia': -1,
  'san antonio': -0.5,
  'san diego': -0.5,
  'dallas': -0.5,
  'miami': -0.5,
  'atlanta': -0.5,
  'boston': -1,
  'seattle': -0.5,
  'san francisco': -0.5,
  'denver': -0.5,
  // Remote areas - small surcharge
  'anchorage': 5,
  'fairbanks': 7,
  'honolulu': 5,
  'juneau': 6,
};

// Zone multipliers for distance-based pricing
const zoneMultipliers: Record<number, number> = {
  1: 1.0,   // Local - base rate
  2: 1.2,   // Regional - 20% increase
  3: 1.5,   // Cross-country - 50% increase
};

export function getShippingZone(state: string): number {
  if (!state) return 2;
  const trimmed = state.trim();
  // Try exact match first
  if (stateZones[trimmed] !== undefined) return stateZones[trimmed];
  // Try uppercase abbreviation
  if (stateZones[trimmed.toUpperCase()] !== undefined) return stateZones[trimmed.toUpperCase()];
  // Try case-insensitive match
  const lowerState = trimmed.toLowerCase();
  for (const [key, zone] of Object.entries(stateZones)) {
    if (key.toLowerCase() === lowerState) return zone;
  }
  // Default to zone 2
  return 2;
}

export function getCityAdjustment(city: string): number {
  if (!city) return 0;
  const lowerCity = city.trim().toLowerCase();
  return cityAdjustments[lowerCity] || 0;
}

function getWeightMultiplier(weightOz: number): number {
  for (const bracket of weightBrackets) {
    if (weightOz <= bracket.maxOz) {
      return bracket.multiplier;
    }
  }
  return weightBrackets[weightBrackets.length - 1].multiplier;
}

export interface CarrierServiceRate {
  carrier: string;
  service: string;
  name: string;
  price: number;
  days: string;
}

/**
 * Calculate shipping rates by weight for all carriers
 * @param weightOz - Total package weight in ounces
 * @param state - Destination state
 * @param city - Destination city
 * @returns Array of carrier service rates
 */
export function calculateShippingRatesByWeight(
  weightOz: number,
  state: string,
  city: string
): CarrierServiceRate[] {
  const zone = getShippingZone(state);
  const zoneMultiplier = zoneMultipliers[zone] || 1.2;
  const weightMultiplier = getWeightMultiplier(weightOz);
  const cityAdj = getCityAdjustment(city);

  const rates: CarrierServiceRate[] = [];

  for (const carrierData of carrierRatesByWeight) {
    for (const [serviceKey, service] of Object.entries(carrierData.services)) {
      const basePrice = service.baseRate * weightMultiplier * zoneMultiplier;
      const finalPrice = Math.max(0, basePrice + cityAdj);
      
      rates.push({
        carrier: carrierData.carrier,
        service: serviceKey,
        name: service.name,
        price: Math.round(finalPrice * 100) / 100,
        days: service.daysMin === service.daysMax 
          ? `${service.daysMin} jour${service.daysMin > 1 ? 's' : ''}`
          : `${service.daysMin}–${service.daysMax} jours`,
      });
    }
  }

  return rates;
}

export interface CalculatedShippingRate {
  standard: number;
  express: number;
  overnight: number;
  zone: number;
  zoneLabel: string;
}

/**
 * Calculate shipping rates (legacy function, now supports weight)
 * @param state - Destination state
 * @param city - Destination city
 * @param subtotal - Order subtotal (unused, kept for compatibility)
 * @param totalWeightOz - Optional total weight in ounces
 * @returns Calculated shipping rates
 */
export function calculateShippingRates(
  state: string, 
  city: string, 
  subtotal: number,
  totalWeightOz?: number
): CalculatedShippingRate {
  const zone = getShippingZone(state);
  const rates = zoneRates[zone] || zoneRates[2];
  const cityAdj = getCityAdjustment(city);

  // If weight is provided, apply weight-based multiplier
  let weightMultiplier = 1.0;
  if (totalWeightOz !== undefined && totalWeightOz > 0) {
    weightMultiplier = getWeightMultiplier(totalWeightOz);
  }

  const standard = Math.max(0, (rates.standardRate * weightMultiplier) + cityAdj);
  const express = Math.max(0, (rates.expressRate * weightMultiplier) + cityAdj);
  const overnight = Math.max(0, (rates.overnightRate * weightMultiplier) + cityAdj);

  return {
    standard: Math.round(standard * 100) / 100,
    express: Math.round(express * 100) / 100,
    overnight: Math.round(overnight * 100) / 100,
    zone,
    zoneLabel: rates.label,
  };
}

// US states list for dropdown
export const usStates = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];